import React from 'react';

const PrivacyPolicy: React.FC = () => (
  <div style={{ maxWidth: 800, margin: '40px auto', padding: '24px', fontFamily: 'serif', color: '#222' }}>
    <h1>Privacy Policy</h1>
    <p>Last updated: June 2025</p>

    <h2>1. Introduction</h2>
    <p>StudyMind ("we," "our," or "us") is committed to protecting the privacy and security of your personal information. This Privacy Policy describes how we collect, use, disclose, and safeguard your information when you use our educational platform and services (collectively, the "Service").</p>

    <h2>2. Information We Collect</h2>
    <h3>2.1 Personal Information</h3>
    <ul>
      <li>Email address and contact information provided during registration and account creation</li>
      <li>Account credentials and authentication data</li>
      <li>Profile information and preferences</li>
      <li>Payment and billing information (processed securely through third-party payment processors)</li>
    </ul>

    <h3>2.2 Usage Information</h3>
    <ul>
      <li>Log data including IP addresses, browser type, operating system, and access timestamps</li>
      <li>Usage patterns, feature interactions, and service utilization data</li>
      <li>Device information and technical specifications</li>
      <li>Error logs and performance metrics</li>
    </ul>

    <h3>2.3 Educational Content</h3>
    <ul>
      <li>Documents, notes, and study materials uploaded to the Service</li>
      <li>Generated flashcards, study sessions, and learning progress data</li>
      <li>Chat interactions and AI-generated responses</li>
      <li>Study goals, preferences, and educational objectives</li>
    </ul>

    <h2>3. How We Use Your Information</h2>
    <h3>3.1 Service Provision</h3>
    <ul>
      <li>To provide, maintain, and improve our educational services</li>
      <li>To process transactions and manage your account</li>
      <li>To generate personalized educational content and recommendations</li>
      <li>To facilitate communication and support services</li>
    </ul>

    <h3>3.2 Legal and Security Purposes</h3>
    <ul>
      <li>To comply with applicable laws, regulations, and legal processes</li>
      <li>To protect against fraud, abuse, and security threats</li>
      <li>To enforce our Terms and Conditions and other policies</li>
      <li>To investigate and respond to user complaints or disputes</li>
    </ul>

    <h3>3.3 Analytics and Improvement</h3>
    <ul>
      <li>To analyze usage patterns and improve service functionality</li>
      <li>To develop new features and educational tools</li>
      <li>To conduct research and statistical analysis (in anonymized form)</li>
      <li>To optimize user experience and platform performance</li>
    </ul>

    <h2>4. Information Sharing and Disclosure</h2>
    <h3>4.1 Service Providers</h3>
    <ul>
      <li>We may share information with trusted third-party service providers who assist in operating our platform, including cloud storage providers, payment processors, and analytics services</li>
      <li>All service providers are contractually obligated to maintain the confidentiality and security of your information</li>
      <li>Service providers are prohibited from using your information for any purpose other than providing services to StudyMind</li>
    </ul>

    <h3>4.2 Legal Requirements</h3>
    <ul>
      <li>We may disclose your information if required by law, regulation, or legal process</li>
      <li>We may share information to protect our rights, property, or safety, or that of our users or the public</li>
      <li>We may disclose information in connection with a merger, acquisition, or sale of assets</li>
    </ul>

    <h3>4.3 Consent-Based Sharing</h3>
    <ul>
      <li>We will not sell, rent, or trade your personal information to third parties for marketing purposes</li>
      <li>Any sharing of information beyond what is described in this policy will only occur with your explicit consent</li>
    </ul>

    <h2>5. Data Security</h2>
    <ul>
      <li>We implement appropriate technical and organizational security measures to protect your information against unauthorized access, alteration, disclosure, or destruction</li>
      <li>Security measures include encryption, access controls, regular security assessments, and employee training</li>
      <li>While we strive to protect your information, no method of transmission over the internet or electronic storage is 100% secure</li>
      <li>We cannot guarantee absolute security and disclaim liability for any unauthorized access or use of your information</li>
    </ul>

    <h2>6. Data Retention</h2>
    <ul>
      <li>We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this policy</li>
      <li>Account information is retained while your account is active and for a reasonable period thereafter</li>
      <li>Educational content and usage data may be retained for service improvement and analytics purposes</li>
      <li>We may retain certain information for legal, regulatory, or business purposes even after account deletion</li>
    </ul>

    <h2>7. Your Rights and Choices</h2>
    <h3>7.1 Access and Control</h3>
    <ul>
      <li>You have the right to access, update, or correct your personal information through your account settings</li>
      <li>You may request deletion of your account and associated data, subject to legal and business requirements</li>
      <li>You can opt out of certain communications and marketing materials</li>
    </ul>

    <h3>7.2 Data Portability</h3>
    <ul>
      <li>You may request a copy of your personal information in a portable format</li>
      <li>We will provide your data in a structured, commonly used, and machine-readable format</li>
    </ul>

    <h3>7.3 Communication Preferences</h3>
    <ul>
      <li>You can manage your communication preferences through your account settings</li>
      <li>You may unsubscribe from marketing communications at any time</li>
      <li>Service-related communications may still be sent as necessary for account management</li>
    </ul>

    <h2>8. International Data Transfers</h2>
    <ul>
      <li>Your information may be transferred to and processed in countries other than your country of residence</li>
      <li>We ensure that such transfers comply with applicable data protection laws</li>
      <li>We implement appropriate safeguards to protect your information during international transfers</li>
    </ul>

    <h2>9. Children's Privacy</h2>
    <ul>
      <li>Our Service is not intended for children under the age of 13</li>
      <li>We do not knowingly collect personal information from children under 13</li>
      <li>If we become aware that we have collected information from a child under 13, we will take steps to delete such information</li>
      <li>Parents or guardians who believe we have collected information from a child under 13 should contact us immediately</li>
    </ul>

    <h2>10. Cookies and Tracking Technologies</h2>
    <ul>
      <li>We use cookies and similar technologies to enhance your experience and analyze usage patterns</li>
      <li>You can control cookie settings through your browser preferences</li>
      <li>Disabling cookies may affect the functionality of our Service</li>
      <li>We do not use cookies to track your activity across third-party websites</li>
    </ul>

    <h2>11. Third-Party Links and Services</h2>
    <ul>
      <li>Our Service may contain links to third-party websites or services</li>
      <li>We are not responsible for the privacy practices of third-party websites or services</li>
      <li>We encourage you to review the privacy policies of any third-party sites you visit</li>
    </ul>

    <h2>12. Changes to This Privacy Policy</h2>
    <ul>
      <li>We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws</li>
      <li>We will notify you of material changes by posting the updated policy on our website</li>
      <li>Your continued use of the Service after changes become effective constitutes acceptance of the updated policy</li>
      <li>We encourage you to review this policy periodically</li>
    </ul>

    <h2>13. Contact Information</h2>
    <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us at:</p>
    <ul>
      <li>Email: <a href="mailto:admin@study-mind.com">admin@study-mind.com</a></li>
      {/* <li>Address: [Your Business Address]</li>
      <li>Phone: [Your Contact Number]</li> */}
    </ul>

    <h2>14. Governing Law and Jurisdiction</h2>
    <ul>
      <li>This Privacy Policy is governed by and construed in accordance with the laws of the jurisdiction in which StudyMind operates</li>
      <li>Any disputes arising from this policy shall be resolved in the courts of the applicable jurisdiction</li>
      <li>If any provision of this policy is found to be unenforceable, the remaining provisions shall remain in full force and effect</li>
    </ul>

    <h2>15. Company Information</h2>
    <p>StudyMind is owned and operated by ALGO-5 Limited. All data processing activities described in this Privacy Policy are conducted by ALGO-5 Limited in accordance with applicable data protection laws and regulations.</p>
  </div>
);

export default PrivacyPolicy; 